import { DatePipe, Location } from '@angular/common';
import { afterNextRender, Component, ElementRef, inject, Injector, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, timeout, TimeoutError } from 'rxjs';

import { GraphService } from '@webgate/angular-azure-core';
import {
  API_ENTITY_SAVE_TIMEOUT_MS,
  API_ENTITY_SAVE_TIMEOUT_MESSAGE
} from 'src/app/constants/api-save-timeout.constants';
import {
  AUDIT_EMAIL_WARNING_DELAY_MS,
  AUDIT_EMAIL_WARNING_DURATION_MS
} from 'src/app/constants/audit-detail-email-warning.constants';
import { DEFAULT_SNACKBAR_DURATION_MS } from 'src/app/constants/snackbar.constants';
import { RECOMMENDATION_INVALID_FORM_FIELD_LABELS } from 'src/app/constants/recommendation-detail-form-validation.constants';
import { UserService } from 'src/app/services/user.service';
import { RecommendationFactory } from 'src/app/utils/factories/RecommendationFactory';
import { navigateToInvalidField } from 'src/app/utils/invalid-field-navigation.helper';
import {
  flushPendingInvalidFieldNavigation,
  requestInvalidFieldNavigation
} from 'src/app/utils/pending-invalid-field-navigation.helper';
import { resolveRecommendationInvalidFieldTabIndex } from 'src/app/utils/recommendation-invalid-field-navigation.helper';
import { getApiErrorDisplayText } from 'src/app/utils/api-error.util';
import { buildInvalidFormState, InvalidEnabledField } from 'src/app/utils/form-validation.helper';
import { showWarningSnackbars } from 'src/app/utils/warning-snackbar.util';
import { v4 as uuid } from 'uuid';

import { Audit } from '../../models/audit/audit';
import { Auditor } from '../../models/auditor';
import { Constants } from '../../models/constants';
import { HiddenFields } from '../../models/hiddenfields';
import { Recommendation } from '../../models/recommendation/recommendation';
import { State } from '../../models/states';
import { AccessService } from '../../services/access.service';
import { AuditService } from '../../services/audit.service';
import { RecommendationService } from '../../services/recommendation.service';
import { observableNext } from '../../utils';
import { BeforeCloseComponent } from '../before-close/before-close.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { HeaderComponent } from '../header/header.component';
import { InvalidFormSummaryComponent } from '../invalid-form-summary/invalid-form-summary.component';

import { RecommendationAssessorsProcessingComponent } from './recommendation-assessors-processing/recommendation-assessors-processing.component';
import { RecommendationAuditorsProcessingComponent } from './recommendation-auditors-processing/recommendation-auditors-processing.component';
import { RecommendationHiddenFieldsComponent } from './recommendation-hidden-fields/recommendation-hidden-fields.component';
import { RecommendationLogsComponent } from './recommendation-logs/recommendation-logs.component';
import { RecommendationsBasicDefinitionsComponent } from './recommendations-basic-definitions/recommendations-basic-definitions.component';

@Component({
  selector: 'app-recommendation-detail',
  templateUrl: './recommendation-detail.component.html',
  styleUrls: ['./recommendation-detail.component.scss'],
  imports: [
    HeaderComponent,
    MatButton,
    MatTabGroup,
    FormsModule,
    ReactiveFormsModule,
    MatTab,
    RecommendationAssessorsProcessingComponent,
    RecommendationAuditorsProcessingComponent,
    RecommendationsBasicDefinitionsComponent,
    FileUploadComponent,
    RecommendationLogsComponent,
    RecommendationHiddenFieldsComponent,
    InvalidFormSummaryComponent,
    MatProgressSpinner
  ],
  providers: [DatePipe]
})
export class RecommendationDetailComponent implements OnInit {
  private recommendationService = inject(RecommendationService);
  private route = inject(ActivatedRoute);
  private auditService = inject(AuditService);
  private location = inject(Location);
  private router = inject(Router);
  private accessService = inject(AccessService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private injector = inject(Injector);
  userService = inject(UserService);
  graphService = inject(GraphService);
  dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar);
  recommendationFactory = inject(RecommendationFactory);

  currentTab = 0;
  recommendations!: Recommendation;
  datePipe = new DatePipe('en_EU');
  fileLinkId!: string;
  assessmentEditable = false;
  auditorEditable = false;
  priorities = Constants.priorities;
  nav!: string;
  isNewRecommendation = false;
  // First column of the big list
  bigLists = HiddenFields.bigLists;
  // Second column of the big list
  firstValues = [];
  // Third column of the big list
  secondValues = [];

  // First column of the last list
  smallLists = HiddenFields.smallLists;
  // Second column of the last list
  smallFirstValues = [];
  // Last column of the last list
  smallSecondValues = HiddenFields.smallSecondValues;
  prolongation_text = '';
  finalLists: string[] = [];
  finalSmallLists: string[] = [];
  isSaving = false;
  form!: UntypedFormGroup;

  audit!: Audit;
  hasFormChanged = false;

  error!: string;
  invalidFields: InvalidEnabledField[] = [];
  private pendingInvalidFieldNavigation?: string;

  options!: UntypedFormControl;

  arrayModActions!: string[];
  arrayModDates!: string[];
  arrayModStatuses!: string[];
  // TODO: Skipped for migration because:
  //  There are references to this query that cannot be migrated automatically.
  @ViewChildren(FileUploadComponent) fileUploader!: QueryList<FileUploadComponent>;

  ngOnInit() {
    const hasId = !!this.route.snapshot.params.id;
    this.isNewRecommendation = !hasId;
    this.getAllValues();
  }

  getAllValues(): void {
    this.accessService.initRoles();
    if (this.route.snapshot.params.audit) {
      this.isNewRecommendation = true;
      if (window.history.state.audit) {
        this.audit = window.history.state.audit;
        this.buildRecommendation();
      } else {
        this.auditService.getDataById(this.route.snapshot.params.audit).subscribe({
          next: (auditEx) => {
            this.audit = auditEx;
            window.history.state.audit = this.audit;
            this.buildRecommendation();
          }
        });
      }
    } else {
      this.buildRecommendation();
    }
  }

  buildFormGroup() {
    this.assessmentEditable = this.chkAssessmentEditable();
    this.auditorEditable = this.chkAuditorEditable();
    this.nav = this.recommendations.auditor === Auditor.GPIA ? '1' : '2';
    this.form = this.recommendationFactory.build(this.assessmentEditable, this.auditorEditable, this.recommendations);
    this.form.valueChanges.subscribe(() => {
      this.updateFormErrorMessage();
      this.hasFormChanged = true;
    });
  }

  chkAssessmentEditable() {
    if (this.recommendations.status !== State.PENDING_OVERDUE && this.recommendations.status !== State.PENDING) {
      return false;
    }
    if (this.recommendations.assessmentCoordinator !== this.userService.userProfile()?.id) {
      return false;
    }
    switch (this.recommendations.auditor) {
      case Auditor.GPIA:
        return this.accessService.isGgpiaAssessmentCoordinator();
      case Auditor.GPRM:
        return this.accessService.isGprmAssessmentCoordinator();
      default:
        return false;
    }
  }

  chkAuditorEditable() {
    if (this.isNewRecommendation) return true;
    switch (this.recommendations.auditor) {
      case Auditor.GPIA:
        return this.accessService.isGpiaAuditor();
      case Auditor.GPRM:
        return this.accessService.isGprmAuditor();
      default:
        return false;
    }
  }

  validation(): boolean {
    return this.form.valid;
  }

  goBack() {
    this.location.back();
  }

  /**
   * Validates the form and saves the recommendation. On API error, displays the error message and reason (e.g. permissions) in a snackbar.
   */
  sendRecommendation() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    this.form.updateValueAndValidity();
    if (this.validation()) {
      this.form.enable();
      this.form.controls['sequenceNumber1'].setValue(Number(this.form.controls['sequenceNumber1'].value));
      this.form.controls['sequenceNumber2'].setValue(Number(this.form.controls['sequenceNumber2'].value));

      const newRecommendation = this.form.value as Recommendation;
      if (!newRecommendation.status) {
        newRecommendation.status = State.PENDING;
      }
      this.setFinallySettledDate();

      this.recommendationService
        .postDataById(newRecommendation)
        .pipe(
          timeout(API_ENTITY_SAVE_TIMEOUT_MS),
          finalize(() => {
            this.isSaving = false;
          })
        )
        .subscribe({
          next: (response) => {
            const recommendation = response.recommendation;
            this.hasFormChanged = false;
            this.form.get('id')?.setValue(recommendation.id);
            this.form.removeControl('current_prolongationStatus');
            this.recommendations.title = recommendation.title;
            this.recommendations.id = recommendation.id;
            this.snackBar.open('Save successful!', undefined, {
              duration: 2000
            });
            this.showSaveEmailWarnings(response.emailWarnings);
            if (this.isNew()) {
              this.location.back();
            }
            this.recommendations = newRecommendation;
            this.form = this.recommendationFactory.build(
              this.assessmentEditable,
              this.auditorEditable,
              this.recommendations
            );
          },
          error: (err) => {
            const message = err instanceof TimeoutError ? API_ENTITY_SAVE_TIMEOUT_MESSAGE : getApiErrorDisplayText(err);
            this.snackBar.open(message, undefined, {
              panelClass: 'error',
              duration: DEFAULT_SNACKBAR_DURATION_MS
            });
          }
        });
    } else {
      this.form.markAllAsTouched();
      this.updateFormErrorMessage();
      this.isSaving = false;
    }
  }

  setFinallySettledDate() {
    const adjournedDate = this.form.controls['adjournedDate'].value;
    if (adjournedDate) {
      this.form.controls['settledDateFinally'].setValue(adjournedDate);
    }
  }

  /** Shows delayed warning snackbars (mail / timeout warnings), one snackbar per message. */
  private showSaveEmailWarnings(emailWarnings?: string[]): void {
    showWarningSnackbars(
      this.snackBar,
      emailWarnings,
      AUDIT_EMAIL_WARNING_DURATION_MS,
      AUDIT_EMAIL_WARNING_DELAY_MS
    );
  }

  isEditMode(): boolean {
    return this.assessmentEditable || this.auditorEditable;
  }

  isProlongationStatus(prolo_status: string) {
    return this.recommendations.prolongationStatus === prolo_status;
  }

  isOverdue(time: string): boolean {
    const today = new Date();
    const deadline = new Date(time);
    return deadline >= today;
  }

  radioButton(): boolean {
    return this.recommendations.isLegallyRequired;
  }

  buildRecommendation(): void {
    // ALL values of a specific element
    if (this.isNewRecommendation) {
      this.handleNewRecommendation();
    } else {
      this.handleExistingRecommendation();
    }
  }

  handleNewRecommendation(): void {
    this.createNewRecommendation();
    this.mergeArray(this.bigLists, this.firstValues, this.secondValues, this.finalLists);
    this.mergeArray(this.smallLists, this.smallFirstValues, this.smallSecondValues, this.finalSmallLists);
    this.buildFormGroup();
    this.form.enable();
    this.form.valueChanges.subscribe(() => {
      this.recommendations = this.form.value;
    });
  }

  handleExistingRecommendation(): void {
    this.recommendationService.getDataById(this.route.snapshot.params.id).subscribe((rec) => {
      this.fileLinkId = rec.fileLinkId;
      this.recommendations = rec;

      if (!rec.fileLinkId) {
        this.fileLinkId = rec.fileLinkId = uuid();
      } else {
        this.fileLinkId = rec.fileLinkId;
        observableNext<QueryList<FileUploadComponent>>(this.fileUploader.changes, (v) => {
          if (v.length !== 1) {
            return false;
          }
          v.toArray()[0].loadFiles();
          return true;
        });
      }
      this.buildFormGroup();
      this.mergeArray(this.bigLists, this.firstValues, this.secondValues, this.finalLists);
      this.mergeArray(this.smallLists, this.smallFirstValues, this.smallSecondValues, this.finalSmallLists);
    });
  }

  async createNewRecommendation() {
    this.recommendations = new Recommendation();
    this.fileLinkId = this.recommendations.fileLinkId = uuid();
    this.recommendations.auditId = this.audit?.id;
    this.recommendations.auditor = this.audit.auditor;
    this.recommendations.reportingYear = this.audit.reportingYear;
    this.recommendations.legalUnit = this.audit.legalUnit;
    this.recommendations.department = this.audit.department;
    this.recommendations.isSpecialAudit = this.audit.isSpecialAudit;
    this.recommendations.concernsName = this.audit.concernsName;
    this.recommendations.concernsCountry = this.audit.concernsCountry;
    this.recommendations.legalUnitResponsibles = this.audit.legalUnitResponsibles;
    this.recommendations.plant = this.audit.plant;
    this.recommendations.assessmentCoordinator = this.audit.assessmentCoordinator;
    this.recommendations.assessmentCoordinatorName = this.audit.assessmentCoordinatorName;
    this.recommendations.emailAddress = this.audit.emailAddress;
  }

  isNew(): boolean {
    return this.isNewRecommendation;
  }

  showSpinner(): boolean {
    return !this.isDefined() || this.isSaving;
  }

  isDefined() {
    if (this.form) {
      return true;
    }
    return false;
  }

  isGPIA(): boolean {
    if (this.recommendations) {
      return this.recommendations.auditDefinition.includes('GPIA');
    } else {
      return false;
    }
  }

  isStatus(status: string): boolean {
    if (this.recommendations) {
      return this.recommendations.status === status;
    } else {
      return false;
    }
  }

  isAuditor(auditor: string) {
    return this.recommendations.auditor === auditor;
  }

  updateTabs(evt: number): void {
    this.currentTab = evt;

    this.flushPendingInvalidFieldNavigation();
  }

  mergeArray(arr1: string[], arr2: string[], arr3: string[], finalArray: string[]) {
    arr1.forEach((k) => {
      finalArray.push(k);
    });
  }

  putBackToPending() {
    this.form.enable();
    this.form.controls['status'].setValue(State.PENDING);
    this.recommendations.status = State.PENDING;
    this.recommendations.adjournedReason = '';
    this.recommendations.amountCapex = null;
    this.form.controls['shortText1'].setValue('');
    this.form.controls['settledBy'].setValue('');
    const settledRejectReason = this.form.controls['settledRejectReason']?.value ?? '';
    this.saveStateAndNavigate(State.PENDING, settledRejectReason);
  }

  finallySettleRecommendation() {
    this.form.enable();
    this.form.controls['status'].setValue(State.FINALYSETTLED);
    this.recommendations.status = State.FINALYSETTLED;
    // SettledByFinally and SettledByFinallyName are automatically set by the backend
    this.saveStateAndNavigate(State.FINALYSETTLED);
  }

  /**
   * Updates recommendation state (e.g. PENDING, FINALYSETTLED) and navigates on success.
   * On API error, displays the error message and reason (e.g. permissions) in a snackbar.
   * @param status - New status value.
   * @param settledRejectReason - Optional reason when settling or rejecting.
   */
  private saveStateAndNavigate(status: string, settledRejectReason?: string) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    const id = this.form.get('id')?.value;
    if (!id) {
      this.isSaving = false;
      return;
    }
    this.recommendationService.setStatus(id, status, settledRejectReason).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.hasFormChanged = false;
        const recommendation = response.recommendation;
        this.recommendations.status = recommendation.status;
        this.recommendations.settledRejectReason = recommendation.settledRejectReason;
        this.snackBar.open('Save successful!', undefined, { duration: 2000 });
        this.showSaveEmailWarnings(response.emailWarnings);
        this.router.navigate(['/recommendations/', this.form.get('auditor')?.value]);
      },
      error: (err) => {
        this.snackBar.open(getApiErrorDisplayText(err), undefined, {
          panelClass: 'error',
          duration: 5000
        });
        this.isSaving = false;
      }
    });
  }

  openDialog() {
    if (this.hasFormChanged) {
      const dialogRef = this.dialog.open(BeforeCloseComponent, {
        data: {
          formGroup: this.form
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.sendRecommendation();
          if (!this.isNew()) {
            this.location.back();
          }
        }
      });
    } else if (this.isNew()) {
      this.router.navigate(['/audits/', this.form.controls['auditor'].value]);
    } else {
      this.location.back();
    }
  }

  private updateFormErrorMessage(): void {
    if (!this.form.invalid) {
      this.error = '';
      this.invalidFields = [];
      return;
    }

    const invalidFormState = buildInvalidFormState(this.form, RECOMMENDATION_INVALID_FORM_FIELD_LABELS);
    this.invalidFields = invalidFormState.invalidFields;
    this.error = invalidFormState.error;
  }

  goToInvalidField(fieldName: string): void {
    requestInvalidFieldNavigation({
      fieldName,
      targetTab: resolveRecommendationInvalidFieldTabIndex(fieldName, this.auditorEditable),
      currentTab: this.currentTab,
      setPendingField: (pendingFieldName) => this.setPendingInvalidFieldNavigation(pendingFieldName),
      activateTab: (tabIndex) => this.activateTab(tabIndex),
      navigateWithinCurrentTab: (fieldNameToNavigate) => this.navigateWithinCurrentTab(fieldNameToNavigate)
    });
  }

  private navigateWithinCurrentTab(fieldName: string): void {
    navigateToInvalidField({
      fieldName,
      rootElement: this.elementRef.nativeElement,
      querySelector: '[formcontrolname="{fieldName}"]'
    });
  }

  private activateTab(tabIndex: number): void {
    this.currentTab = tabIndex;

    this.flushPendingInvalidFieldNavigation();
  }

  private flushPendingInvalidFieldNavigation(): void {
    flushPendingInvalidFieldNavigation({
      pendingField: this.pendingInvalidFieldNavigation,
      clearPendingField: () => this.clearPendingInvalidFieldNavigation(),
      scheduleAfterRender: (callback) => this.scheduleAfterNextRender(callback),
      navigateWithinCurrentTab: (fieldName) => this.navigateWithinCurrentTab(fieldName)
    });
  }

  private setPendingInvalidFieldNavigation(fieldName: string): void {
    this.pendingInvalidFieldNavigation = fieldName;
  }

  private clearPendingInvalidFieldNavigation(): void {
    this.pendingInvalidFieldNavigation = undefined;
  }

  private scheduleAfterNextRender(callback: () => void): void {
    afterNextRender(callback, { injector: this.injector });
  }
}
